import React, { useState } from 'react';

// The "Export Options" dialog, shared by the Stock page, a location and a sub-location.
//
// This started as one block on Stock and was copy-pasted twice; the copies then drifted —
// only the Stock one guarded an empty device list, and only the Stock one told the user
// anything when the clipboard was unavailable. Everything renders from here now, so the
// three stay honest. Callers vary only in the subtitle and in which extra actions they
// add (a report, an XLSX with their own columns) via `actions`.
//
// Serials and device ids are joined with ", " because that is what the OWL portal's bulk
// fields expect on paste.

const OPT_BTN = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px',
  borderRadius: 10, border: '1px solid var(--border-default)', background: 'var(--surface-inset)',
  color: 'var(--text-default)', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
  fontFamily: 'inherit', textAlign: 'left',
};

const copyIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>;
const tickIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#00c875" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>;
export const downloadIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>;
export const reportIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>;
export const historyIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3" /><circle cx="12" cy="12" r="10" /></svg>;

function ExportOptionsModal({ open, onClose, devices, subtitle, subtitleAccent, actions = [] }) {
  const [copiedField, setCopiedField] = useState(null);

  if (!open) return null;

  const devs = devices || [];
  const none = devs.length === 0;

  const close = () => { setCopiedField(null); onClose(); };

  const hoverIn = e => { if (!none) e.currentTarget.style.background = 'var(--surface-raised)'; };
  const hoverOut = e => { e.currentTarget.style.background = 'var(--surface-inset)'; };
  const btnStyle = { ...OPT_BTN, cursor: none ? 'default' : 'pointer', opacity: none ? 0.5 : 1 };

  // navigator.clipboard is undefined on non-HTTPS origins and rejects when the document
  // isn't focused — surface that instead of leaving the user thinking nothing was copied.
  const copy = (text, field) => {
    if (!navigator.clipboard) { alert("Copying isn't available in this browser context. Use the file export instead."); return; }
    navigator.clipboard.writeText(text).then(
      () => { setCopiedField(field); setTimeout(close, 1200); },
      () => { alert("Couldn't copy to the clipboard. Click the page and try again, or use the file export."); },
    );
  };

  const copyBtn = (field, label, value) => (
    <button key={field} disabled={none} onClick={() => copy(devs.map(value).join(', '), field)}
      style={btnStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
      {copiedField === field ? tickIcon : copyIcon}
      <span style={{ color: copiedField === field ? '#00c875' : 'var(--text-default)' }}>
        {copiedField === field ? 'Copied!' : label}
      </span>
    </button>
  );

  return (
    <>
      <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 300, backdropFilter: 'blur(3px)' }} />
      <div role="dialog" aria-modal="true" aria-label="Export Options"
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 301, background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 14, width: 'min(340px,calc(100vw - 32px))', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '18px 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', letterSpacing: '-0.2px' }}>Export Options</div>
            {subtitle && (
              <div style={{ fontSize: 11, color: subtitleAccent ? 'var(--brand-default)' : 'var(--text-muted)', marginTop: 3, fontWeight: subtitleAccent ? 600 : 400 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button onClick={close} aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, padding: 0, flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-raised)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div style={{ padding: '8px 24px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {none && <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 2 }}>No devices match the current filters.</div>}
          {/* A report describes the whole set, so it renders above the per-device copies
              where a caller supplies one. */}
          {actions.filter(a => a.first).map(a => (
            <button key={a.label} disabled={none} onClick={() => { a.onClick(); close(); }}
              style={btnStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
              {a.icon}{a.label}
            </button>
          ))}
          {copyBtn('serials', 'Copy Serial Numbers', d => d.serial)}
          {copyBtn('deviceIds', 'Copy Device IDs', d => d.deviceId || d.id)}
          {actions.filter(a => !a.first).map(a => (
            <button key={a.label} disabled={none} onClick={() => { a.onClick(); close(); }}
              style={btnStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
              {a.icon}{a.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export default ExportOptionsModal;
